/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { getImageFromDataUrl } from 'cio/utils/images';
import Ember from 'ember';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import config from 'cio/config/environment';
import { buildWaiter } from '@ember/test-waiters';
import type { Task } from 'ember-concurrency';

let waiter = buildWaiter('cio-async-modal:promise-waiter');

interface CioAsyncModalModalSignature<T = void> {
  Element: HTMLDivElement;
  Args: {
    pendingTitle: string;
    pendingImage: string;
    successTitle: string;
    successImage: string;
    successText?: string;
    successRoute?: string;
    failureTitle: string;
    failureImage: string;
    onStart: () => Promise<T>;
    onClose: () => void;
    mediaContainerId?: string;
  };
  Blocks: {
    default: [promise: Task<undefined, [T]>];
  };
}

export type ResultType = {
  status: 'pending' | 'failure' | 'success';
  image: string | null;
  title: string;
};

export default class CioAsyncModal extends Component<CioAsyncModalModalSignature> {
  @tracked result: ResultType = {
    status: 'pending',
    image: '',
    title: '',
  };

  @tracked error = '';

  @tracked showSuccessLink = false;

  async handlePendingState() {
    let result: ResultType = {
      image: null,
      status: 'pending',
      title: this.args.successTitle,
    };
    if (this.args.pendingImage != null) {
      await getImageFromDataUrl(this.args.pendingImage);
      result.image = this.args.pendingImage;
    }
    result.title = this.pendingTitle;
    this.result = result;
  }

  async handleSuccessState() {
    let result: ResultType = {
      image: null,
      status: 'success',
      title: this.args.successTitle,
    };
    if (this.args.successImage != null) {
      await getImageFromDataUrl(this.args.successImage);
      result.image = this.args.successImage;
    }
    result.title = this.successTitle;
    this.result = result;
    this.showSuccessLink = this.successText.length > 0 && this.successRoute.length > 0;
  }

  async handleErrorState(error: any) {
    let result: ResultType = {
      image: null,
      status: 'failure',
      title: this.args.failureTitle,
    };
    if (this.args.failureImage) {
      await getImageFromDataUrl(this.args.failureImage);
      result.image = this.args.failureImage;
    }
    this.error = error;
    this.result = result;
  }

  promise = task({ drop: true }, async () => {
    if (!Ember.testing) {
      await timeout(config.APP.feedbackDelay);
    }
    try {
      let result = await this.args.onStart();
      await this.handleSuccessState();
      return result;
    } catch (e) {
      await this.handleErrorState(e);
      throw e;
    }
  });

  get pendingTitle() {
    return this.args.pendingTitle ?? '';
  }

  get successTitle() {
    return this.args.successTitle ?? '';
  }

  get successText() {
    return this.args.successText ?? '';
  }

  get successRoute() {
    return this.args.successRoute ?? '';
  }

  get failureTitle() {
    return this.args.failureTitle ?? '';
  }

  @action
  async start() {
    let token = waiter.beginAsync();

    try {
      await this.handlePendingState();
      await this.promise.perform();
    } catch (e) {
      await this.handleErrorState(e);
    } finally {
      waiter.endAsync(token);
    }
  }

  @action
  retry() {
    this.reset();
    this.start();
  }

  @action
  reset() {
    this.error = '';
    this.promise.cancelAll({ resetState: true });
  }

  @action
  closeModal() {
    this.args.onClose();
  }
}
